import { Logger } from "./logger";
import { COOKIE_TEST, COOKIE_PATH, COOKIE_SAME_SITE } from "./config";

function canUseCookies(): boolean {
  if (typeof document === "undefined") return false;
  try {
    const testKey = COOKIE_TEST;
    document.cookie = `${testKey}=1;path=${COOKIE_PATH}`;
    const works = document.cookie.indexOf(testKey) !== -1;
    document.cookie = `${testKey}=;path=${COOKIE_PATH};expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    return works;
  } catch {
    return false;
  }
}

const cookiesAvailable = canUseCookies();

function shouldUseSecure(): boolean {
  if (COOKIE_SAME_SITE === "None") return true;
  if (typeof location !== "undefined" && location.protocol === "https:") return true;
  return false;
}

const useSecure = shouldUseSecure();

let cookieWarningLogged = false;

export const CookieManager = {
  get(name: string): string | null {
    if (!cookiesAvailable) {
      if (!cookieWarningLogged) {
        Logger.warn("Cookies not available in this environment");
        cookieWarningLogged = true;
      }
      return null;
    }
    const encodedName = encodeURIComponent(name);
    /* eslint-disable security/detect-non-literal-regexp */
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapeRegex(encodedName)}=([^;]*)`));
    /* eslint-enable security/detect-non-literal-regexp */
    if (!match) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  },

  set(name: string, value: string, days: number): void {
    if (!cookiesAvailable) {
      if (!cookieWarningLogged) {
        Logger.warn("Cookies not available — cannot set: " + name);
        cookieWarningLogged = true;
      }
      return;
    }
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    const secure = useSecure ? ";Secure" : "";
    const cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)};path=${COOKIE_PATH};expires=${expires};SameSite=${COOKIE_SAME_SITE}${secure}`;
    document.cookie = cookie;
    Logger.log(`Cookie set: ${name} (expires in ${days}d)`);
  },

  delete(name: string): void {
    if (!cookiesAvailable) return;
    const secure = useSecure ? ";Secure" : "";
    document.cookie = `${encodeURIComponent(name)}=;path=${COOKIE_PATH};expires=Thu, 01 Jan 1970 00:00:00 GMT;SameSite=${COOKIE_SAME_SITE}${secure}`;
    Logger.log(`Cookie deleted: ${name}`);
  },

  has(name: string): boolean {
    return this.get(name) !== null;
  },
};

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
